// src/components/Board/AiSuggestionPanel.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useProjectContext } from '../../contexts/ProjectContext';
import styles from '../../assets/styles/components/AiSuggestionPanel.module.scss';
import SuggestionModal from '../Suggestion/SuggestionModal';

import type { LlmSuggestionItem } from '../../types/chat';

import { ReactComponent as GoogleAdsIcon } from '../../assets/icons/icon-google-ads.svg';
import { ReactComponent as MetaAdsIcon } from '../../assets/icons/icon-meta-ads.svg';
import { ReactComponent as YahooIcon } from '../../assets/icons/icon-yahoo.svg';
import { ReactComponent as TrashIcon } from '../../assets/icons/icon-trash.svg';
import { ChevronLeftIcon } from '../../assets/icons/ChevronLeftIcon';
import { formatMonthDayTime } from '../../utils/date';
import { PLATFORM_LABELS } from '../../utils/constants';

interface SuggestionCampaign {
  campaign_id: string | number;
  campaign_name: string;
  before_budget?: number | string;
  current_budget?: number;
  suggested_budget?: number;
  before_status?: string;
  current_status?: string;
  suggested_status?: string;
}

interface SuggestionDevice {
  device: string;
  current_modifier: number;
  suggested_modifier: number;
}

interface SuggestionAd {
  text: string;
}

interface SuggestionBreadcrumbItem {
  level: 'account' | 'campaign' | 'adGroup' | 'cross_platform' | 'platform';
  label: string;
}

interface AiSuggestionPanelProps {
  suggestionId: number | null;
  chatId: string | null;
}

const renderPlatformIcon = (platform?: string) => {
  const plat = String(platform || '').toUpperCase();
  if (plat === 'GOOGLE') {
    return <GoogleAdsIcon className={styles.platformIcon} />;
  }
  if (plat === 'META') {
    return <MetaAdsIcon className={styles.platformIcon} />;
  }
  if (plat === 'YAHOO') {
    return <YahooIcon className={styles.platformIcon} />;
  }
  return null;
};

const AiSuggestionPanel: React.FC<AiSuggestionPanelProps> = ({ suggestionId, chatId }) => {
  const { selectedProjectId } = useProjectContext();
  const [suggestionList, setSuggestionList] = useState<LlmSuggestionItem[]>([]);
  const [selectedSuggestionId, setSelectedSuggestionId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletedSuggestionIds, setDeletedSuggestionIds] = useState<number[]>([]);
  const [appliedSuggestionIds, setAppliedSuggestionIds] = useState<number[]>([]);
  const [isSuggestionModalOpen, setIsSuggestionModalOpen] = useState(false);

  const fetchSuggestions = async () => {
    if (!selectedProjectId) return;
    setLoading(true);
    setError(null);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/get-suggestions?project_id=${selectedProjectId}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('提案データの取得に失敗しました。');
      const list = (await res.json()) as LlmSuggestionItem[];
      setSuggestionList(list);
    } catch (err) {
      console.error('Fetch Suggestions Error:', err);
      setError('提案データの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuggestions();
  }, [selectedProjectId, chatId, suggestionId]);

  useEffect(() => {
    if (suggestionId) {
      setSelectedSuggestionId(suggestionId);
    }
  }, [suggestionId]);

  const activeSuggestions = useMemo(() => {
    if (!chatId) return [];
    return suggestionList.filter(
      (item) => item.chat_id === chatId && !deletedSuggestionIds.includes(item.id)
    );
  }, [suggestionList, chatId, deletedSuggestionIds]);

  const suggestion = useMemo(() => {
    if (!selectedSuggestionId) return null;
    const found = suggestionList.find((item) => item.id === selectedSuggestionId) || null;
    if (!found) return null;
    return appliedSuggestionIds.includes(found.id) ? { ...found, status: 'APPLIED' } : found;
  }, [suggestionList, selectedSuggestionId, appliedSuggestionIds]);

  const suggestionData = useMemo(() => {
    if (!suggestion) return null;
    const detail = suggestion.suggestion || {};
    const payload = detail.payload && typeof detail.payload === 'object' ? detail.payload : {};
    return {
      ...detail,
      campaigns: payload.campaigns ?? [],
      devices: payload.devices ?? [],
      suggestions: payload.suggestions ?? [],
      keywords: payload.keywords ?? [],
      ads: payload.ads ?? [],
    };
  }, [suggestion]);

  const handleOpenApplyModal = () => {
    if (!suggestion || suggestion.status === 'APPLIED') return;
    setIsSuggestionModalOpen(true);
  };

  const handleCloseApplyModal = () => {
    setIsSuggestionModalOpen(false);
  };

  const handleApplied = async () => {
    if (suggestion) {
      setAppliedSuggestionIds((prev) =>
        prev.includes(suggestion.id) ? prev : [...prev, suggestion.id]
      );
      setSuggestionList((prev) =>
        prev.map((item) => (item.id === suggestion.id ? { ...item, status: 'APPLIED' } : item))
      );
    }
    await fetchSuggestions();
  };

  const handleDeleted = async () => {
    setSelectedSuggestionId(null);
    await fetchSuggestions();
  };

  const handleDelete = async () => {
    if (!suggestion) return;
    if (!window.confirm('この最適化提案を削除しますか？')) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/delete-suggestion?suggestion_id=${suggestion.id}`,
        { method: 'POST', credentials: 'include' }
      );
      if (res.ok) {
        setDeletedSuggestionIds((prev) => [...prev, suggestion.id]);
        setSelectedSuggestionId(null);
      } else {
        alert('提案の削除に失敗しました。');
      }
    } catch (e) {
      console.error('Delete Suggestion Error:', e);
      alert('通信エラーが発生しました。');
    }
  };

  const renderChanges = () => {
    if (!suggestionData) return null;
    const formType = suggestionData.formType || 'budget';

    if (formType === 'budget') {
      return (
        <div className={styles.detailFormWrap}>
          <div className={styles.detailFormSectionTitle}>変更提案内容</div>
          {(suggestionData.campaigns || []).map((c: SuggestionCampaign, i: number) => {
            const beforeBudgetValue = c.before_budget ?? c.current_budget;
            const suggestedBudgetValue = c.suggested_budget;
            const currentBudget = beforeBudgetValue
              ? `¥${Number(beforeBudgetValue).toLocaleString()}`
              : '未設定';
            const suggestedBudget = suggestedBudgetValue
              ? `¥${Number(suggestedBudgetValue).toLocaleString()}`
              : '未設定';
            return (
              <div key={i} className={styles.detailFormRow}>
                <span className={styles.rowTitle}>{c.campaign_name}</span>
                <div className={styles.comparisonBox}>
                  <div className={styles.adBefore}>
                    <span className={styles.subLabel}>現在</span>
                    <span className={styles.valueText}>{currentBudget}</span>
                  </div>
                  <span className={styles.arrowIcon}>➜</span>
                  <div className={styles.adAfter}>
                    <span className={styles.subLabelPink}>提案</span>
                    <span className={styles.valueText}>{suggestedBudget}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    if (formType === 'campaign_status') {
      return (
        <div className={styles.detailFormWrap}>
          <div className={styles.detailFormSectionTitle}>変更提案内容</div>
          {(suggestionData.campaigns || []).map((c: SuggestionCampaign, i: number) => {
            const beforeStatusValue = c.before_status ?? c.current_status;
            const currentStatus = beforeStatusValue === 'PAUSED' ? '一時停止' : '配信中';
            const suggestedStatus = c.suggested_status === 'PAUSED' ? '一時停止' : '配信中';
            return (
              <div key={i} className={styles.detailFormRow}>
                <span className={styles.rowTitle}>{c.campaign_name}</span>
                <div className={styles.comparisonBox}>
                  <div className={styles.adBefore}>
                    <span className={styles.subLabel}>現在</span>
                    <span className={styles.statusBadge}>{currentStatus}</span>
                  </div>
                  <span className={styles.arrowIcon}>➜</span>
                  <div className={styles.adAfter}>
                    <span className={styles.subLabelPink}>提案</span>
                    <span className={`${styles.statusBadge} ${styles.pink}`}>
                      {suggestedStatus}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    if (formType === 'device_targeting') {
      return (
        <div className={styles.detailFormWrap}>
          <div className={styles.detailFormSectionTitle}>変更提案内容</div>
          <div className={styles.detailFormRow}>
            <span className={styles.rowTitle}>{suggestionData.campaign_name}</span>
            <div className={styles.detailFormRowCol}>
              {(suggestionData.devices || []).map((d: SuggestionDevice, i: number) => {
                const curMod =
                  d.current_modifier > 0 ? `+${d.current_modifier}%` : `${d.current_modifier}%`;
                const sugMod =
                  d.suggested_modifier > 0
                    ? `+${d.suggested_modifier}%`
                    : `${d.suggested_modifier}%`;
                return (
                  <div key={i} className={styles.comparisonBox}>
                    <span className={`${styles.rowTitle} ${styles.deviceLabel}`}>{d.device}</span>
                    <div className={styles.adBefore}>
                      <span className={styles.subLabel}>現在</span>
                      <span className={styles.valueText}>{curMod}</span>
                    </div>
                    <span className={styles.arrowIcon}>➜</span>
                    <div className={styles.adAfter}>
                      <span className={styles.subLabelPink}>提案</span>
                      <span className={styles.valueText}>{sugMod}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    if (formType === 'ad_text') {
      return (
        <div className={styles.detailFormWrap}>
          <div className={styles.detailFormSectionTitle}>変更提案内容</div>
          <div className={styles.adComparison}>
            <div className={styles.adBefore}>
              <span className={styles.subLabel}>現在の広告グループ設定</span>
              <p className={styles.valueText}>{suggestionData.ad_group_name || '-'}</p>
            </div>
            <div className={`${styles.arrowIcon} ${styles.verticalArrow}`}>➜</div>
            <div className={styles.adAfter}>
              <span className={styles.subLabelPink}>追加提案テキスト（見出し）</span>
              <ul className={styles.valueText}>
                {(suggestionData.ads || []).map((ad: SuggestionAd, i: number) => (
                  <li key={i}>{ad.text}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.detailFormWrap}>
        <div className={styles.detailFormSectionTitle}>変更提案内容</div>
        <p className={styles.valueText}>設定変更の詳細はチャットでの指示に従い適用してください。</p>
      </div>
    );
  };

  if (selectedSuggestionId && suggestion) {
    const detail = suggestion.suggestion || {};
    const title = detail.title || suggestion.suggestion_title || '最適化提案';
    const comment = detail.reason || '';
    const platform = detail.platform || 'GOOGLE';
    const createdAt = suggestion.created_at || '';
    const breadcrumbs = Array.isArray(detail.breadcrumbs)
      ? (detail.breadcrumbs as SuggestionBreadcrumbItem[])
      : [];

    const dateStr = formatMonthDayTime(createdAt);
    const showWarning = Boolean(suggestion.is_in_learning_phase);

    return (
      <>
        <div className={styles.suggestionPanel}>
          <div className={styles.detailHeader}>
            <button
              type="button"
              className={styles.backButton}
              onClick={() => {
                setSelectedSuggestionId(null);
                setError(null);
              }}
            >
              <ChevronLeftIcon />
              <span>戻る</span>
            </button>
            <div className={styles.detailHeaderRight}>
              <span className={styles.detailDate}>{dateStr} 更新</span>
              <button
                type="button"
                className={styles.detailDeleteIconBtn}
                onClick={handleDelete}
                title="提案を非表示にする"
              >
                <TrashIcon />
              </button>
            </div>
          </div>

          <div className={styles.panelContent}>
            <div className={styles.headerBreadcrumbs}>
              {breadcrumbs.length > 0 ? (
                breadcrumbs.map((crumb, idx) => (
                  <React.Fragment key={`${crumb.level}-${idx}`}>
                    {idx === 0 && renderPlatformIcon(platform)}
                    <span
                      className={
                        crumb.level === 'platform' ? styles.crumbPlatform : styles.crumbAccount
                      }
                    >
                      {crumb.label}
                    </span>
                    {idx < breadcrumbs.length - 1 && (
                      <span className={styles.crumbSeparator}>&gt;</span>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <>
                  {renderPlatformIcon(platform)}
                  <span className={styles.crumbPlatform}>
                    {PLATFORM_LABELS[platform] || platform}
                  </span>
                </>
              )}
            </div>

            <div className={styles.detailTitleArea}>
              <h3 className={styles.detailTitle}>{title}</h3>
            </div>

            {showWarning && (
              <div className={styles.warningBanner}>
                ⚠️
                最終更新から2週間未満のアカウントです。配信設定の過度な変更は学習をリセットさせる可能性があるため、慎重にご判断ください。
              </div>
            )}

            <div className={styles.commentSection}>
              <div className={styles.sectionLabel}>Aibexからの提案内容</div>
              <p className={styles.commentText}>{comment}</p>
            </div>

            {renderChanges()}

            <div className={styles.suggestionActionArea}>
              {error && <div className={styles.errorText}>{error}</div>}
              <div className={styles.btnRowSingle}>
                <button
                  type="button"
                  className={styles.actionButton}
                  onClick={handleOpenApplyModal}
                  disabled={suggestion.status === 'APPLIED'}
                >
                  {suggestion.status === 'APPLIED' ? '適用済み' : '作業依頼'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <SuggestionModal
          open={isSuggestionModalOpen}
          onClose={handleCloseApplyModal}
          activeSuggestion={suggestion}
          projectId={selectedProjectId}
          onApplied={handleApplied}
          onDeleted={handleDeleted}
        />
      </>
    );
  }

  return (
    <div className={styles.suggestionPanel}>
      <div className={styles.panelHeader}>
        <span>AI提案</span>
      </div>
      <div className={styles.panelContent}>
        {activeSuggestions.length === 0 ? (
          <div className={styles.suggestionPlaceholder}>
            <p>
              診断項目の「提案を作成する」ボタンを押すか、チャットで提案作成を依頼するとここに提案内容が表示されます。
            </p>
          </div>
        ) : (
          <div className={styles.suggestionListWrap}>
            {activeSuggestions.map((item) => {
              const detail = (item.suggestion || {}) as Record<string, any>;
              const title = detail.title || item.suggestion_title || '最適化提案';
              const dateStr = formatMonthDayTime(item.created_at);

              return (
                <div
                  key={item.id}
                  className={`${styles.suggestionCard} ${item.status === 'APPLIED' || appliedSuggestionIds.includes(item.id) ? styles.appliedCard : ''}`}
                  onClick={() => setSelectedSuggestionId(item.id)}
                >
                  <div className={styles.cardHeader}>
                    <span className={styles.cardTitle}>{title}</span>
                  </div>
                  <div className={styles.cardMeta}>
                    {renderPlatformIcon(detail.platform || 'GOOGLE')}
                    <span className={styles.metaDate}>{dateStr}</span>
                    <span
                      className={`${styles.cardStatus} ${item.status === 'APPLIED' || appliedSuggestionIds.includes(item.id) ? styles.applied : styles.pending}`}
                    >
                      {item.status === 'APPLIED' || appliedSuggestionIds.includes(item.id)
                        ? '適用済み'
                        : '未適用'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AiSuggestionPanel;
